import React, { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useCommonContext } from "contexts/CommonContext";
import CreateQuestDrawer from "./questManagement/CreateQuestDrawer";
import DeleteQuestModal from "./questManagement/DeleteQuestModal";
import QuestPagination from "./questManagement/QuestPagination";
import QuestProgressFilter from "./questManagement/QuestProgressFilter";
import QuestProgressDetailModal from "./questManagement/QuestProgressDetailModal";
import QuestProgressTable from "./questManagement/QuestProgressTable";
import { MainTab, QuestTab } from "./questManagement/QuestTabs";
import QuestTable from "./questManagement/QuestTable";
import SectionTitle from "./questManagement/SectionTitle";
import StatusQuestModal from "./questManagement/StatusQuestModal";
import {
  BADGE_TIER_LABELS,
  QUEST_PAGE_SIZE,
  QUEST_PROGRESS_PAGE_SIZE,
} from "common/constants";
import type {
  AdminCompanyQuestProgressItem,
  AdminQuest,
  CreateQuestPayload,
  MainTabKey,
  QuestProgressType,
  QuestTabKey,
  SelectOption,
} from "common/types/questManagement.type";
import {
  getPaginationItems,
  getQuestCategory,
  getRequestErrorMessage,
  getQuestSaveErrorMessage,
  isDefaultHiddenQuest,
} from "common/questManagement.utils";
import {
  useCreateAdminQuestMutation,
  useDeleteAdminQuestMutation,
  useGetAdminBadgesQuery,
  useGetAdminCompanyQuestProgressDetailQuery,
  useGetAdminCompanyQuestProgressQuery,
  useGetAdminQuestsQuery,
  useLazyGetAdminQuestNextCodeQuery,
  useUpdateAdminQuestMutation,
  useUpdateAdminQuestStatusMutation,
} from "reducers/questManagementApi";
import { getPublicAssetUrl } from "components/productSellerCompanyCard/productSellerCompanyCard.utils";

const QUEST_FETCH_ERROR_MESSAGE = "クエストデータの取得に失敗しました。";
const BADGE_FETCH_ERROR_MESSAGE = "バッジデータの取得に失敗しました。";
const PROGRESS_FETCH_ERROR_MESSAGE = "ユーザー進捗データの取得に失敗しました。";
const PROGRESS_DETAIL_FETCH_ERROR_MESSAGE =
  "ユーザー進捗詳細データの取得に失敗しました。";
const QUEST_DELETE_ERROR_MESSAGE =
  "クエストの削除に失敗しました。時間を置いて再度お試しください。";
const QUEST_STATUS_ERROR_MESSAGE =
  "クエストの状態変更に失敗しました。時間を置いて再度お試しください。";
const DEFAULT_HIDDEN_QUEST_LOCKED_MESSAGE =
  "初期設定の隠しクエストは削除できません。";
const QUEST_CREATE_SUCCESS_MESSAGE = "クエストを作成しました。";
const QUEST_UPDATE_SUCCESS_MESSAGE = "クエストを更新しました。";
const QUEST_DELETE_SUCCESS_MESSAGE = "クエストを削除しました。";
const QUEST_FREEZE_SUCCESS_MESSAGE = "クエストを凍結しました。";
const QUEST_UNFREEZE_SUCCESS_MESSAGE = "クエストの凍結を解除しました。";

const QuestManagementPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [activeMainTab, setActiveMainTab] = useState<MainTabKey>("settings");
  const [activeQuestTab, setActiveQuestTab] = useState<QuestTabKey>("monthly");
  const [progressDetailCompanyId, setProgressDetailCompanyId] = useState<
    string | null
  >(null);
  const [page, setPage] = useState(1);
  const [progressPage, setProgressPage] = useState(1);
  const [progressType, setProgressType] = useState<QuestProgressType>("monthly");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [nextQuestCode, setNextQuestCode] = useState("");
  const [isQuestDrawerOpen, setIsQuestDrawerOpen] = useState(false);
  const [questDrawerInstanceKey, setQuestDrawerInstanceKey] = useState(0);
  const [editingQuest, setEditingQuest] = useState<AdminQuest | null>(null);
  const [deletingQuest, setDeletingQuest] = useState<AdminQuest | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [statusQuest, setStatusQuest] = useState<AdminQuest | null>(null);
  const [statusErrorMessage, setStatusErrorMessage] = useState("");
  const [togglingQuestIds, setTogglingQuestIds] = useState<Set<string>>(
    () => new Set(),
  );

  const {
    data: quests = [],
    isLoading,
    isError,
    refetch: refetchQuests,
  } = useGetAdminQuestsQuery();
  const {
    data: badges = [],
    isLoading: isBadgeLoading,
    isError: isBadgeError,
    refetch: refetchBadges,
  } = useGetAdminBadgesQuery();
  const {
    data: progressData,
    isLoading: isProgressLoading,
    isError: isProgressError,
    refetch: refetchProgressData,
  } = useGetAdminCompanyQuestProgressQuery(
    {
      type: progressType,
      page: progressPage,
      limit: QUEST_PROGRESS_PAGE_SIZE,
    },
    { skip: activeMainTab !== "progress" },
  );
  const {
    data: progressDetail = null,
    isLoading: isProgressDetailLoading,
    isError: isProgressDetailError,
    refetch: refetchProgressDetail,
  } = useGetAdminCompanyQuestProgressDetailQuery(
    {
      companyId: progressDetailCompanyId ?? "",
      type: progressType,
    },
    { skip: !progressDetailCompanyId },
  );
  const [fetchNextQuestCode] = useLazyGetAdminQuestNextCodeQuery();
  const [createAdminQuest, { isLoading: isCreatingQuest }] =
    useCreateAdminQuestMutation();
  const [updateAdminQuest, { isLoading: isUpdatingQuest }] =
    useUpdateAdminQuestMutation();
  const [deleteAdminQuest, { isLoading: isDeletingQuest }] =
    useDeleteAdminQuestMutation();
  const [updateAdminQuestStatus] = useUpdateAdminQuestStatusMutation();

  const progressRows = progressData?.items ?? [];
  const progressTotalPages = Math.max(1, progressData?.meta.total_pages || 1);
  const isSavingQuest = isCreatingQuest || isUpdatingQuest;
  const errorMessage = isError ? QUEST_FETCH_ERROR_MESSAGE : "";
  const badgeErrorMessage = isBadgeError ? BADGE_FETCH_ERROR_MESSAGE : "";
  const progressErrorMessage = isProgressError
    ? PROGRESS_FETCH_ERROR_MESSAGE
    : "";
  const progressDetailErrorMessage = isProgressDetailError
    ? PROGRESS_DETAIL_FETCH_ERROR_MESSAGE
    : progressDetailCompanyId && !isProgressDetailLoading && !progressDetail
      ? PROGRESS_DETAIL_FETCH_ERROR_MESSAGE
      : "";

  useEffect(() => {
    setHeaderTitle("クエスト管理");
  }, [setHeaderTitle]);

  useEffect(() => {
    setPage(1);
  }, [activeQuestTab]);

  useEffect(() => {
    setProgressPage(1);
  }, [progressType]);

  const fetchInitialData = () => {
    refetchQuests();
    refetchBadges();
  };

  const fetchProgressData = () => {
    refetchProgressData();
  };

  const badgeOptions = useMemo<SelectOption[]>(() => {
    return badges.map((badge) => ({
      label: `${badge.name}（${BADGE_TIER_LABELS[badge.tier] ?? badge.tier}）`,
      value: String(badge.id),
      iconUrl: getPublicAssetUrl(badge.icon_url),
    }));
  }, [badges]);

  useEffect(() => {
    badgeOptions.forEach((option) => {
      if (!option.iconUrl) return;
      const image = new Image();
      image.src = option.iconUrl;
    });
  }, [badgeOptions]);

  const filteredQuests = useMemo(() => {
    return quests.filter((quest) => {
      return getQuestCategory(quest) === activeQuestTab;
    });
  }, [activeQuestTab, quests]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredQuests.length / QUEST_PAGE_SIZE),
  );

  const paginatedQuests = useMemo(() => {
    const startIndex = (page - 1) * QUEST_PAGE_SIZE;
    return filteredQuests.slice(startIndex, startIndex + QUEST_PAGE_SIZE);
  }, [filteredQuests, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginationItems = useMemo(
    () => getPaginationItems(page, totalPages),
    [page, totalPages],
  );

  const progressPaginationItems = useMemo(
    () => getPaginationItems(progressPage, progressTotalPages),
    [progressPage, progressTotalPages],
  );

  const handleOpenProgressDetail = (row: AdminCompanyQuestProgressItem) => {
    setProgressDetailCompanyId(row.company_id);
  };

  const handleCloseProgressDetail = () => {
    setProgressDetailCompanyId(null);
  };

  const handleRetryProgressDetail = () => {
    if (!progressDetailCompanyId) return;
    refetchProgressDetail();
  };

  const tableTitle =
    activeQuestTab === "monthly" ? "マンスリークエスト一覧" : "隠し実績一覧";

  const handleOpenCreate = async () => {
    setEditingQuest(null);
    setSaveErrorMessage("");
    setNextQuestCode("");
    setQuestDrawerInstanceKey((current) => current + 1);
    setIsQuestDrawerOpen(true);

    try {
      const code = await fetchNextQuestCode().unwrap();
      setNextQuestCode(code);
    } catch (error) {
      console.error("Failed to fetch next quest code:", error);
    }
  };

  const handleOpenEdit = (quest: AdminQuest) => {
    setEditingQuest(quest);
    setSaveErrorMessage("");
    setIsQuestDrawerOpen(true);
  };

  const handleCancelSave = () => {
    setSaveErrorMessage("");
    setIsQuestDrawerOpen(false);
    setEditingQuest(null);
    setNextQuestCode("");
  };

  const handleSaveQuest = async (payload: CreateQuestPayload) => {
    setSaveErrorMessage("");

    try {
      if (editingQuest) {
        const updatePayload: Omit<CreateQuestPayload, "code"> = {
          quest_type: payload.quest_type,
          title: payload.title,
          description: payload.description,
          action_type: payload.action_type,
          condition_type: payload.condition_type,
          target_count: payload.target_count,
          reward_xp: payload.reward_xp,
          target_role: payload.target_role,
          active_start_at: payload.active_start_at,
          active_end_at: payload.active_end_at,
          reward_badge_id: payload.reward_badge_id,
          is_hidden: payload.is_hidden,
          is_active: payload.is_active,
        };

        await updateAdminQuest({
          questId: editingQuest.id,
          payload: updatePayload,
        }).unwrap();
        messageApi.success(QUEST_UPDATE_SUCCESS_MESSAGE);
      } else {
        await createAdminQuest(payload).unwrap();
        messageApi.success(QUEST_CREATE_SUCCESS_MESSAGE);
      }

      setIsQuestDrawerOpen(false);
      setEditingQuest(null);
      setNextQuestCode("");
    } catch (error) {
      console.error("クエストの保存に失敗しました:", error);
      setSaveErrorMessage(getQuestSaveErrorMessage(error));
    }
  };

  const handleOpenDelete = (quest: AdminQuest) => {
    if (isDefaultHiddenQuest(quest)) {
      messageApi.error(DEFAULT_HIDDEN_QUEST_LOCKED_MESSAGE);
      return;
    }

    setDeletingQuest(quest);
    setDeleteErrorMessage("");
  };

  const handleCancelDelete = () => {
    if (isDeletingQuest) return;
    setDeletingQuest(null);
    setDeleteErrorMessage("");
  };

  const handleConfirmDelete = async () => {
    if (!deletingQuest) return;

    setDeleteErrorMessage("");
    try {
      await deleteAdminQuest(deletingQuest.id).unwrap();
      setDeletingQuest(null);
      messageApi.success(QUEST_DELETE_SUCCESS_MESSAGE);
    } catch (error) {
      console.error("Failed to delete admin quest:", error);
      setDeleteErrorMessage(
        getRequestErrorMessage(error, QUEST_DELETE_ERROR_MESSAGE),
      );
    }
  };

  const handleOpenStatusChange = (quest: AdminQuest) => {
    setStatusQuest(quest);
    setStatusErrorMessage("");
  };

  const handleCancelStatusChange = () => {
    if (!statusQuest) return;
    if (togglingQuestIds.has(String(statusQuest.id))) return;
    setStatusQuest(null);
    setStatusErrorMessage("");
  };

  const handleConfirmStatusChange = async () => {
    if (!statusQuest) return;

    const questId = String(statusQuest.id);

    setTogglingQuestIds((current) => new Set(current).add(questId));
    setStatusErrorMessage("");

    try {
      const willFreezeQuest = statusQuest.is_active;

      await updateAdminQuestStatus({
        questId,
        is_active: !willFreezeQuest,
      }).unwrap();
      setStatusQuest(null);
      messageApi.success(
        willFreezeQuest
          ? QUEST_FREEZE_SUCCESS_MESSAGE
          : QUEST_UNFREEZE_SUCCESS_MESSAGE,
      );
    } catch (error) {
      console.error("Failed to toggle admin quest status:", error);
      setStatusErrorMessage(
        getRequestErrorMessage(error, QUEST_STATUS_ERROR_MESSAGE),
      );
    } finally {
      setTogglingQuestIds((current) => {
        const next = new Set(current);
        next.delete(questId);
        return next;
      });
    }
  };

  return (
    <div className="relative min-h-[924px] self-stretch overflow-x-hidden bg-white pb-[100px]">
      {messageContextHolder}

      <div className="border-b border-[#d5d5d5]">
        <div className="flex overflow-x-auto">
          <MainTab
            active={activeMainTab === "settings"}
            onClick={() => setActiveMainTab("settings")}
          >
            クエスト設定・編集
          </MainTab>
          <MainTab
            active={activeMainTab === "progress"}
            onClick={() => setActiveMainTab("progress")}
          >
            ユーザー進捗・ログ
          </MainTab>
        </div>
      </div>

      {activeMainTab === "settings" ? (
        <div className="px-5 py-7 sm:px-10 sm:py-10">
          <SectionTitle title="クエスト一覧" />

          <div className="mt-5 border-b border-[#d5d5d5] sm:mt-10">
            <div className="flex overflow-x-auto">
              <QuestTab
                active={activeQuestTab === "monthly"}
                widthClass="w-[150px] sm:w-[194px]"
                onClick={() => setActiveQuestTab("monthly")}
              >
                マンスリークエスト
              </QuestTab>
              <QuestTab
                active={activeQuestTab === "hidden"}
                widthClass="w-[110px] sm:w-[114px]"
                onClick={() => setActiveQuestTab("hidden")}
              >
                隠し実績
              </QuestTab>
            </div>
          </div>

          <QuestTable
            errorMessage={errorMessage}
            isLoading={isLoading}
            onCreateClick={handleOpenCreate}
            onDeleteClick={handleOpenDelete}
            onEditClick={handleOpenEdit}
            onRetry={fetchInitialData}
            onToggleActiveClick={handleOpenStatusChange}
            isCreateDisabled={false}
            quests={paginatedQuests}
            title={tableTitle}
            togglingQuestIds={togglingQuestIds}
          />

          <QuestPagination
            onPageChange={setPage}
            page={page}
            paginationItems={paginationItems}
            totalPages={totalPages}
          />
        </div>
      ) : (
        <div className="px-5 py-5 sm:px-10 sm:py-10">
          <QuestProgressFilter
            value={progressType}
            onChange={setProgressType}
          />

          <QuestProgressTable
            errorMessage={progressErrorMessage}
            isLoading={isProgressLoading}
            onDetailClick={handleOpenProgressDetail}
            onRetry={fetchProgressData}
            rows={progressRows}
          />

          <QuestPagination
            onPageChange={setProgressPage}
            page={progressPage}
            paginationItems={progressPaginationItems}
            totalPages={progressTotalPages}
          />
        </div>
      )}

      <CreateQuestDrawer
        key={questDrawerInstanceKey}
        badgeOptions={badgeOptions}
        badgeErrorMessage={badgeErrorMessage}
        codeSuggestion={nextQuestCode}
        createErrorMessage={saveErrorMessage}
        initialQuest={editingQuest}
        isDefaultHiddenQuest={isDefaultHiddenQuest(editingQuest)}
        isBadgeLoading={isBadgeLoading}
        isCreating={isSavingQuest}
        isOpen={isQuestDrawerOpen}
        mode={editingQuest ? "edit" : "create"}
        onCancel={handleCancelSave}
        onCreate={handleSaveQuest}
        questType={activeQuestTab === "hidden" ? "hidden" : "season"}
      />

      <DeleteQuestModal
        errorMessage={deleteErrorMessage}
        isDeleting={isDeletingQuest}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        quest={deletingQuest}
      />

      <StatusQuestModal
        errorMessage={statusErrorMessage}
        isUpdating={
          statusQuest ? togglingQuestIds.has(String(statusQuest.id)) : false
        }
        onCancel={handleCancelStatusChange}
        onConfirm={handleConfirmStatusChange}
        quest={statusQuest}
      />

      <QuestProgressDetailModal
        detail={progressDetail}
        errorMessage={progressDetailErrorMessage}
        isLoading={isProgressDetailLoading}
        isOpen={Boolean(progressDetailCompanyId)}
        onClose={handleCloseProgressDetail}
        onRetry={handleRetryProgressDetail}
      />
    </div>
  );
};

export default QuestManagementPage;
